import { configurationContext } from './configuration';
import React, { useContext, useMemo } from 'react';
import { IntlProvider as IP } from 'react-intl';
import { intlMessages } from '../i18n';
import {
	getIntlMessages,
	joinIntlMessages,
	sanitizeLocale,
} from '../utils/intl.utils';
import { configContext } from './config/context';

/**
 * Provides react-intl context for the chat lib.
 *
 * Built-in message catalogs (`intlMessages`) are merged with any custom
 * `localization.messages` from configuration. The `custom` bucket has the
 * highest priority and is applied last, overriding every locale-specific
 * value including with an empty string.
 *
 * `fallbackOnEmptyString={false}` is intentional: when a message is
 * explicitly set to `""` (the "disabled" state managed by the configurator),
 * react-intl must render the empty string as-is and must NOT fall back to
 * the `defaultMessage` defined in each descriptor.
 */
export const IntlProvider = ({ children }: React.PropsWithChildren<{}>) => {
	const { locale } = useContext(configContext);
	const { localization: { messages = {} } = {} } =
		useContext(configurationContext);

	const _messages = useMemo(
		() => getIntlMessages(joinIntlMessages(intlMessages, messages), locale),
		[messages, locale],
	);

	return (
		<IP
			locale={sanitizeLocale(locale)}
			messages={_messages}
			fallbackOnEmptyString={false}
			onError={(e) => {
				if (e.code !== 'MISSING_TRANSLATION') {
					throw e;
				}
			}}
		>
			{children}
		</IP>
	);
};
